import { useEffect, useRef, useState } from 'react';
import {
  massstab, zoomKlemmen, ZOOM_GROESSTE, ZOOM_KLEINSTE,
} from './kartenmass.ts';
import {
  ausPixel, kernkante, meterJePixel, nachPixel, punktdichte,
  type Punkt,
} from './kartenebene.ts';

import {
  GRENZPUNKTE_AB_STUFE, GRENZPUNKTE_BIS_PUNKTE,
  grenzpunkteZaehlen, grenzpunkteZeigen, grenzpunktSatz,
} from './grenzpunkte.ts';
export {
  GRENZPUNKTE_AB_STUFE, GRENZPUNKTE_BIS_PUNKTE,
} from './grenzpunkte.ts';
export { grubenRadius } from './kartenebene.ts';
export type { Punkt } from './kartenebene.ts';
export { meterJePixel } from './kartenebene.ts';

/**
 * Die Karte, auf der der Leitungsweg nachgezogen wird.
 *
 * ORIGINALTEXT MARVIN (19.08.2026)
 *   „wir nehmen den Lageplan und zeichnen so grob den leitungsweg der Telekom
 *    ab dem rohrverbsnd zum Kunden nach. Das System hat dann klare Marker,
 *    dadurch die Länge und muss dann nur noch die Abschnitte selbst selektieren
 *    zwecks der Oberfläche und Vorschläge machen was es sein kann."
 *
 * KEINE KARTENBIBLIOTHEK. Die Kachelrechnung nach Web Mercator sind zwanzig
 * Zeilen und steht in `regeln/tiefbau.ts`, damit ein Prüfstand sie nachrechnen
 * kann: eine falsche Kachelrechnung verschiebt die ganze Zeichnung, und das
 * fällt erst auf, wenn der Bagger woanders steht.
 *
 * DAS LUFTBILD WIRD ZWEIMAL GEBRAUCHT: einmal zum Ansehen, einmal zum
 * Abtasten - und seit .105 ist es DASSELBE Zeichenfeld. Bis dahin standen
 * sichtbare `<img>` neben einem versteckten Feld: zwei Wege über dasselbe
 * Bild, und der sichtbare war der unschärfere. Die Kacheln laufen über den
 * eigenen Server, also ist das Feld nicht „getaint" und lässt sich lesen;
 * kommt keine Kachel an, bleiben die Oberflächen auf „unbekannt", und es wird
 * gesagt, warum.
 */

/* DIE KACHELRECHNUNG UND DIE PROJEKTION STEHEN IN `kartenebene.ts`.
 *
 * Sie standen hier, in einer Datei mit JSX - also an einer Stelle, die ein
 * Prüfstand nur mit einer Textsuche erreicht, und **eine Textsuche misst, dass
 * etwas dasteht, nicht, dass es stimmt.** Dieselbe Aufteilung wie bei
 * `namen.ts`, `terminraster.ts`, `klebeband.ts`, `kartenmass.ts` und
 * `oberflaeche.ts`. */

/** Was ein Durchgang über eine Lage ergeben hat. */
interface Bildstand {
  /** Ein Bild ist angekommen und liegt im Zeichenfeld. */
  da: boolean;
  /** Der Durchgang ist zu Ende - vorher sagt `da: false` gar nichts. */
  fertig: boolean;
  /** Wie viele Kacheln der Server dafür brauchte und wie viele er bekam. */
  gesamt: number;
  gut: number;
  /** Aus welcher Stufe der Dienst wirklich geliefert hat. */
  stufe: number | null;
  /** Um wieviel das Bild vergrössert wurde. 1 heisst: gar nicht. */
  vergroessert: number;
  /** Und derselbe Befund in Worten, vom Server formuliert. */
  schaerfe: string | null;
  /** Der Grund, den der Server für eine leere Antwort mitgeschickt hat. */
  grund: string | null;
  /**
   * WIE DIESE LAGE GEZEICHNET WERDEN MUSS - vom Server gemessen.
   *
   * ORIGINALTEXT MARVIN (04.09.2026)
   *   „zeigt straße und hausnummer nicht valide an."
   *
   * `null` heisst: nicht gemessen, es gilt der Regelfall. Der ist
   * multiplizierend, und das ist das Verhalten, das seit .100 trägt - **eine
   * neue Messung darf den Regelfall nicht mit umschreiben.**
   */
  art: 'multiplizierend' | 'normal' | null;
  /** Mittlere Deckkraft der Lage in Prozent, und ihre mittlere Farbe. */
  deckung: number | null;
  tinte: number | null;
  /**
   * Ob ein ZWEITER Abzug daraufgehört.
   *
   * Er verdoppelt die Tiefe jedes Striches - und jeder Fläche. Bei einer
   * Lage, die Gebäude FÜLLT, sind das die schwarzen Dächer vom 04.09.2026.
   * Gemessen wird es auf dem Server; hier steht das Urteil.
   */
  verstaerken: boolean;
  /**
   * OB DIE SCHRIFT AUS DER FLÄCHE HERAUSGEHOLT WURDE.
   *
   * ORIGINALTEXT MARVIN (05.09.2026)
   *   „wie du siehst sind die häuser im luftbild noch zu dunkel, hausnummern
   *    und straßennamen kann man nicht lesen."
   *
   * Die Lage, die die Hausnummern bringt, FÜLLT dabei die Gebäude; der
   * Server trennt beides nach dem TON (`fuellungDaempfen`) und schwächt die
   * Füllung, statt sie zu entfernen. Hier steht nur, DASS es geschehen ist -
   * der Satz unter der Karte braucht es, das Zeichnen nicht: eine gedämpfte
   * Lage kommt ohnehin als „normal" herein.
   */
  gedaempft: boolean;
  /** Wie viel der Deckung Füllung war, in Prozent. */
  fuellung: number | null;
}

const LEER_STAND: Bildstand = {
  da: false, fertig: false, gesamt: 0, gut: 0, stufe: null,
  vergroessert: 1, schaerfe: null, grund: null,
  art: null, deckung: null, tinte: null, verstaerken: true,
  gedaempft: false, fuellung: null,
};

/**
 * EINE LAGE ZEICHNEN - EIN BILD, EINE ANFRAGE.
 *
 * ORIGINALTEXT MARVIN (04.09.2026)
 *   „die zweite meldung friert nun alles ein, es hängt wieder. ich kann
 *    nichts klicken. erst jetzt nach minuten komt die karte wieder."
 *
 * **HIER STANDEN VIERZIG ANFRAGEN JE LAGE.** Ein Ausschnitt von 760 mal 560
 * Anzeigepunkten holt bei doppelter Punktdichte die Stufe `zoom+1`; jede
 * Kachel wird dann halb so gross gezeichnet, also braucht es rund sieben mal
 * sechs Stück - zweiundvierzig für den Grund und zweiundvierzig für die
 * Beschriftung. Jede davon ist eine eigene Anfrage an den eigenen Server,
 * jede löst die Sitzung auf, und jede belegt dabei eine Verbindung zur
 * Datenbank. Danach wartet **jede andere Anfrage der Oberfläche dahinter**.
 *
 * Der Server setzt das Bild jetzt zusammen (`kartenbild` in `geodienst.ts`)
 * und liefert es in genau `breite * dichte` mal `hoehe * dichte` echten
 * Bildpunkten. Damit gilt weiter, was der alte Dialog tut - die Fläche
 * rechnet in Anzeigepunkten und speichert in Bildpunkten -, nur ohne die
 * vierzig Wege dahin. **Und es gibt keine Stelle mehr, an der etwas hoch-
 * oder heruntergerechnet wird, ohne dass es jemand gerechnet hat:** das Bild
 * wird eins zu eins über die ganze Fläche gelegt.
 *
 * DIE STUFENSUCHE IST MITGEZOGEN. Sie stand hier als Schleife über zwölf
 * Runden: holen, im Kopf lesen, dass es diese Stufe nicht gibt, alles noch
 * einmal holen. Der Server weiss es beim ersten Versuch - er hat gefragt.
 */
async function bildZeichnen(
  ktx: CanvasRenderingContext2D,
  weg: string,
  mitte: Punkt, zoom: number, breite: number, hoehe: number, dichte: number,
  lebt: () => boolean
): Promise<Bildstand> {
  const u = `${weg}?lat=${encodeURIComponent(String(mitte.lat))}`
          + `&lng=${encodeURIComponent(String(mitte.lng))}`
          + `&zoom=${zoom}&breite=${Math.round(breite)}`
          + `&hoehe=${Math.round(hoehe)}&dichte=${dichte}`;
  try {
    const a = await fetch(u, { credentials: 'same-origin' });
    if (!lebt()) return { ...LEER_STAND, fertig: false };

    const text = (n: string) => {
      const w = a.headers.get(n);
      if (!w) return null;
      try { return decodeURIComponent(w); } catch { return w; }
    };
    const zahl = (n: string, ersatz: number) => {
      const z = Number(a.headers.get(n));
      return Number.isFinite(z) ? z : ersatz;
    };
    const teile = String(a.headers.get('x-mds-kacheln') ?? '').split('/');
    const gut = Number(teile[0]); const gesamt = Number(teile[1]);
    const kopf: Bildstand = {
      da: false, fertig: true,
      gut: Number.isFinite(gut) ? gut : 0,
      gesamt: Number.isFinite(gesamt) ? gesamt : 0,
      stufe: a.headers.has('x-mds-stufe') ? zahl('x-mds-stufe', 0) : null,
      vergroessert: zahl('x-mds-vergroessert', 1),
      schaerfe: text('x-mds-schaerfe'),
      grund: text('x-mds-grund'),
      art: a.headers.get('x-mds-zeichenart') === 'normal' ? 'normal'
        : a.headers.get('x-mds-zeichenart') === 'multiplizierend'
          ? 'multiplizierend' : null,
      deckung: a.headers.has('x-mds-deckung')
        ? zahl('x-mds-deckung', 100) : null,
      /* Ob ein ZWEITER Abzug daraufgehört. Der Server misst es an der
         Deckung; hier steht nur noch das Urteil. Ohne Kopf gilt das
         Verhalten von .100 bis .108: verstärken. */
      verstaerken: a.headers.has('x-mds-verstaerken')
        ? a.headers.get('x-mds-verstaerken') === 'ja' : true,
      tinte: a.headers.has('x-mds-tinte') ? zahl('x-mds-tinte', 255) : null,
      /* Und ob die Füllung dieser Lage gedämpft wurde. Ohne Kopf heisst
         das: nicht gedämpft - das Verhalten von .111. */
      gedaempft: a.headers.get('x-mds-gedaempft') === 'ja',
      fuellung: a.headers.has('x-mds-fuellung')
        ? zahl('x-mds-fuellung', 0) : null,
    };
    if (!a.ok || a.status === 204) return kopf;

    const bild = await createImageBitmap(await a.blob());
    if (!lebt()) { bild.close?.(); return { ...kopf, fertig: false }; }
    /* EINS ZU EINS. Die Fläche ist `breite*dichte` breit und trägt
       `setTransform(dichte,…)`; das Bild kommt in genau dieser Grösse. */
    ktx.drawImage(bild, 0, 0, breite, hoehe);
    bild.close?.();
    return { ...kopf, da: true };
  } catch (f: any) {
    /* Ein Bild, das nicht durchkommt, hinterlässt hier keine Spur - kein
       kaputtes Bildzeichen, keine schwarze Fläche. Dass es NICHT durchkam,
       steht im Satz daneben: sonst ist ein stummer Dienst von einer leeren
       Gegend nicht zu unterscheiden. */
    return { ...LEER_STAND, fertig: true,
             grund: 'Die Karte war von hier aus nicht zu erreichen.' };
  }
}

export interface Kartenlage { mitte: Punkt; zoom: number; }

/**
 * Ein fertig gezeichnetes Bild mit der Lage, für die es galt.
 *
 * Es überbrückt die Zeit, in der das nächste unterwegs ist. `quelle` ist der
 * Weg, aus dem es stammt: das Luftbild darf nicht als Platzhalter für die
 * Straßenkarte dastehen, sonst hält jemand den Schalter für kaputt.
 */
interface Vorbild {
  bild: HTMLCanvasElement;
  mitte: Punkt;
  zoom: number;
  quelle: string;
}

/** Ein Flurstück, so wie es der Landesdienst liefert. */
export interface Flurstueck { geometry: any; nr: string; }

export default function Tiefbaukarte({
  lage, setzeLage, breite, hoehe, luftbildweg, strassenweg = null,
  beschriftungsweg = null, beschriftung = true, kinder,
  aufKlick, aufProbe, luftbild = true, flurstuecke, nummern = true,
  kartenbefund = null, aufLuftbild,
}: {
  lage: Kartenlage;
  setzeLage: (l: Kartenlage) => void;
  breite: number;
  hoehe: number;
  /**
   * Der Weg zum LUFTBILD - EIN Bild, nicht vierzig Kacheln.
   *
   * Er trägt keine Platzhalter mehr: der Ausschnitt geht als Frage mit
   * (`?lat=…&zoom=…&breite=…&dichte=…`), und der Server setzt das Bild
   * zusammen. Warum, steht bei `bildZeichnen`.
   */
  luftbildweg: string | null;
  /**
   * Die Straßenkarte, sobald das Luftbild aus ist. Dieselbe Vorlage, ein
   * anderer Weg auf dem Server.
   *
   * ORIGINALTEXT MARVIN (02.09.2026)
   *   „3. wenn ich die Luftbilder ausblenden keine Karte angezeigt wird."
   *
   * Bis .79 blieb ohne Luftbild eine weisse Fläche mit den Grenzen darauf.
   * Das war als Flurstücksplan gedacht - und sah aus wie eine Karte, die
   * nicht lädt. Ohne diese Vorlage bleibt es beim alten Bild.
   */
  strassenweg?: string | null;
  /**
   * Die Beschriftung: Straßennamen und Hausnummern, durchsichtig über dem
   * Luftbild.
   *
   * ORIGINALTEXT MARVIN (02.09.2026)
   *   „es fehlt aber der straßenname und die hausnummern"
   *
   * Ein Luftbild zeigt Dächer. Welche Straße das ist und welches Haus die 4a,
   * steht nicht darauf - und genau das braucht der, der die Grube setzt und
   * das Papier danach an die Gemeinde schickt. Ohne diese Vorlage bleibt die
   * Karte, wie sie war.
   *
   * ÜBER DER STRASSENKARTE WIRD SIE NICHT GEBRAUCHT: die trägt ihre
   * Beschriftung selbst, und zweimal übereinander ist sie unleserlich.
   */
  beschriftungsweg?: string | null;
  /**
   * Der Weg, der sagt, WARUM keine Kachel angekommen ist.
   *
   * ORIGINALTEXT MARVIN (03.09.2026)
   *   „die karten bei dem auftrag vorbereiten und planung klappen immernoch
   *    nicht."
   *
   * Bis .92 stand hier „das Luftbild antwortet nicht" und ein Verweis auf
   * einen Knopf in der Konfiguration - an den ein Monteur gar nicht
   * herankommt. Der GRUND lag die ganze Zeit im Kopf der Kachelantwort
   * (`x-mds-grund`), und **ein `<img>` kann keinen Kopf lesen.** Ohne diese
   * Vorlage bleibt es beim alten Satz. */
  kartenbefund?: string | null;
  /** Die Beschriftung anzeigen. */
  beschriftung?: boolean;
  /**
   * Das Luftbild ausblenden.
   *
   * ORIGINALTEXT MARVIN (24.08.2026)
   *   „beim auftrag vorbereiten fehlen die karten, die Grundstücken, bw
   *    flurstücke und das ausblenden der luftbilder."
   *
   * Ohne Luftbild liegt die Straßenkarte darunter, die Grenzen und die
   * Zeichnung bleiben stehen. Für einen Behördenantrag ist das oft die bessere
   * Anlage: ein Plan zeigt genau das, worum es geht, und nicht die
   * Gartenmöbel der Nachbarn.
   */
  luftbild?: boolean;
  /** Die amtlichen Flurstücksgrenzen. Sie liegen UNTER der Zeichnung. */
  flurstuecke?: Flurstueck[] | null;
  /** Die Flurstücksnummern mit anzeigen. Bei vielen Grenzen wird es sonst voll. */
  nummern?: boolean;
  /**
   * Was über die Karte gezeichnet wird, in Bildpunkten relativ zur Ecke.
   *
   * `mpp` sind die Meter je Bildpunkt der aktuellen Stufe. Wer eine Markierung
   * zeichnet, die im Gelände eine Größe hat, rechnet damit - sonst deckt
   * derselbe Ring auf zwei Stufen zwei verschiedene Flächen ab.
   */
  kinder: (nach: (p: Punkt) => { x: number; y: number },
           mpp: number) => React.ReactNode;
  aufKlick?: (p: Punkt) => void;
  /** Meldet das Zeichenfeld, sobald daraus gelesen werden kann. */
  aufProbe?: (leser: ((p: Punkt) => number[] | null) | null) => void;
  /**
   * Den Haken „Luftbild" von hier aus umlegen.
   *
   * Er gehört dem Bildschirm darüber, und das bleibt so: die Karte ändert
   * ihn nicht von selbst. Sie BIETET ihn nur genau dort an, wo die Frage
   * entsteht - wenn die Straßennamen ausbleiben, ist die Straßenkarte der
   * Weg zu ihnen, und ein Knopf an der Meldung erspart die Suche nach dem
   * Haken über der Karte. Ohne diese Vorlage steht dort nur der Satz.
   */
  aufLuftbild?: (an: boolean) => void;
}) {
  const rahmen = useRef<HTMLDivElement>(null);
  /* ZWEI ZEICHENFLÄCHEN STATT EINES HAUFENS `<img>`, und das ist der Kern
     dieser Version. Der Grund wird ANGESEHEN und ABGETASTET - dieselbe
     Fläche, ein Zeichenfeld statt zwei. Die Beschriftung liegt darüber und
     wird multiplizierend eingeblendet, so wie im alten Dialog. */
  const grundFeld = useRef<HTMLCanvasElement>(null);
  const schriftFeld = useRef<HTMLCanvasElement>(null);
  /* Das zuletzt fertig gezeichnete Bild, mit der Lage, für die es galt. Es
     füllt die Zeit, in der das nächste unterwegs ist; ohne es ist dort grau.
     Die QUELLE steht dabei: ein Bild aus einer anderen Lage wäre kein
     Übergang, sondern eine falsche Auskunft. */
  const vorGrund = useRef<Vorbild | null>(null);
  const vorSchrift = useRef<Vorbild | null>(null);
  /* DIE FEINSTE STUFE FÜHRT DER SERVER.
     Hier standen `maxzGrund` und `maxzSchrift` - wörtlich `MAXZ_LUFT` und
     `MAXZ_SCHRIFT` des alten Dialogs - und dazu je ein Merker für die
     Quelle, damit eine Messung über den einen Dienst nicht für den nächsten
     gilt. Beides ist mitgezogen: **der Server FRAGT den Dienst, das Fenster
     hat es aus einem Kopf erfahren und danach alles noch einmal geholt.** */
  /**
   * WÄHREND EINER ZIEH-GESTE WIRD NICHTS NACHGEHOLT.
   *
   * ORIGINALTEXT MARVIN (04.09.2026)
   *   „wenn ich die karte schiebe, dann wird die ganze karte grau, er hat das
   *    nicht vorgeladen oder so"
   *
   * **ER BESCHREIBT ZWEI FEHLER IN EINEM SATZ, UND BEIDE STANDEN HIER.**
   *
   * Der erste: `onPointerMove` rief bei JEDER Mausbewegung `setzeLage`. Damit
   * lief der Zeicheneffekt bei jedem Bildpunkt neu und schickte eine neue
   * Anfrage an den Server - bei einem Zug über die halbe Karte sind das
   * dreissig bis fünfzig Bilder, von denen neunundvierzig niemand sieht.
   * **Was gezogen wird, ist die ANSICHT und nicht die Anfrage.** Der Schub
   * steht jetzt hier, in Anzeigepunkten, und wird als Verschiebung auf die
   * Lagen gelegt; erst beim Loslassen wird EINMAL neu geholt.
   *
   * Der zweite: der Effekt hat die Fläche als ERSTES grau gefüllt und danach
   * geholt. Zwischen beidem liegt die Antwortzeit des Landesdienstes - und
   * genau die ist das Grau. Es gibt kein Bild, das man in dieser Zeit zeigen
   * könnte? Doch: **das vorige.** Es zeigt dieselbe Gegend, nur um den Schub
   * versetzt, und das ist die Auskunft, die ein Mensch beim Ziehen braucht.
   */
  const [schub, setSchub] = useState<{ x: number; y: number } | null>(null);
  const ziehStart = useRef<{ x: number; y: number } | null>(null);
  const [verschoben, setVerschoben] = useState(false);
  const [lesbar, setLesbar] = useState<boolean | null>(null);
  /* WIE VIELE KACHELN DES GRUNDES WIRKLICH ANGEKOMMEN SIND.
   *
   * ORIGINALTEXT MARVIN (02.09.2026)
   *   „bild 4: die kartenansicht ohne luftbild funktioniert überhaupt nicht"
   *
   * Der Weg zur Straßenkarte war gebaut und die Fläche blieb trotzdem weiss:
   * antwortet der Dienst nicht, kommt je Kachel ein 204, das Bildzeichen wird
   * versteckt, und übrig bleibt genau dasselbe Bild wie bei „es gibt hier
   * nichts zu sehen". Ohne diese Zahl sind die beiden nicht zu unterscheiden -
   * und der Satz unter der Karte behauptete weiter „Straßenkarte".
   */
  /* GEZÄHLT WIRD JE KACHEL UND NICHT MIT EINEM ZÄHLER.
   *
   * ORIGINALTEXT MARVIN (03.09.2026, mit einem Bildschirmfoto, auf dem das
   * Luftbild zur Hälfte dasteht)
   *   „die karten bei dem auftrag vorbereiten und planung klappen immernoch
   *    nicht."
   *
   * Auf seinem Bild lagen Kacheln in der Karte UND darüber stand „keine
   * einzige Kachel geliefert". Beides zugleich, und der Kasten hatte unrecht.
   *
   * Hier stand EIN Zähler für den ganzen Ausschnitt, und sein Schlüssel
   * enthält die Ecke und die Anzahl der Kacheln. Beim Verschieben ändert sich
   * dieser Schlüssel, die Zählung fängt bei null an - **aber die Kacheln, die
   * schon dastehen, melden sich nicht noch einmal**: ihr `<img>` behält
   * seinen Schlüssel (`z/x/y`) und damit seinen Knoten, und `onLoad` feuert
   * genau einmal im Leben. Kommt danach eine einzige neue Kachel nicht durch,
   * steht der Zähler auf null, während der halbe Ausschnitt gefüllt ist.
   *
   * Gemerkt wird deshalb JE KACHEL, ob sie angekommen ist. Daraus lassen sich
   * drei Dinge unterscheiden, die vorher eines waren: es ist NICHTS gekommen,
   * es ist ein TEIL gekommen, und es ist noch nicht alles beantwortet.
   *
   * SEIT .105 ZÄHLT DIE ZEICHNUNG SELBST. Die Kacheln kommen mit `fetch` und
   * gehen in ein Zeichenfeld; damit ist am Ende eines Durchgangs bekannt, wie
   * viele angekommen sind, und die drei Ausgänge fallen aus derselben Zahl.
   * Ein Geduldsmesser braucht es dafür nicht mehr - **ein `fetch` antwortet
   * immer, ein `<img>` auf einen hängenden Weg nie.** */
  const [stand, setStand] = useState<Bildstand>(LEER_STAND);

  /* UND DIE BESCHRIFTUNG WIRD GENAUSO GEZÄHLT.
   *
   * ORIGINALTEXT MARVIN (03.09.2026, mit fünf Bildschirmfotos)
   *   „straßennamen und hausnummern stehen auch nicht drauf, das problem ist
   *    immernoch da."
   *
   * **SIE FIEL STILL AUS.** Eine Beschriftungskachel, die nicht durchkam,
   * wurde hier nur versteckt (`visibility: hidden`) - nicht gezählt, nicht
   * genannt, nicht nachgefragt. Der Haken „Beschriftung" blieb gesetzt, die
   * Lage kam nie, und auf dem Bildschirm war das von einer Gegend ohne
   * Straßennamen nicht zu unterscheiden.
   *
   * Der Grundkarte hat dieses Haus genau diese Zählung schon zweimal
   * gegeben (.93 und .96), und die Lage DARÜBER hat sie nicht bekommen -
   * dieselbe Lehre, eine Etage weiter: **wer einen Bereich nicht ansieht,
   * wird über ihn beruhigt.** */
  const [schriftStand, setSchriftStand] = useState<Bildstand>(LEER_STAND);

  /* WARUM NICHTS ANGEKOMMEN IST. Gefragt wird NUR im Fehlerfall und je
     Grundlage einmal: der Weg dahinter holt ein echtes Testbild vom
     Landesdienst, und das ist kein Abruf, den man bei jedem Zeichnen macht. */
  const [warum, setWarum] = useState<{ was: string; text: string } | null>(null);
  /* UND WARUM DIE BESCHRIFTUNG NICHT KOMMT. Eigener Merker, denn beide Lagen
     können gleichzeitig ausfallen und haben zwei verschiedene Gründe: das
     Luftbild kommt vom Land, die Beschriftung vom Bund. Ein gemeinsamer
     Merker zeigte den einen Grund über der anderen Lage. */
  const [warumSchrift, setWarumSchrift] = useState<string | null>(null);

  // Die linke obere Ecke des Ausschnitts, in Weltbildpunkten.
  const mitteP = nachPixel(lage.mitte, lage.zoom);
  const eckeX = mitteP.x - breite / 2;
  const eckeY = mitteP.y - hoehe / 2;

  const nach = (p: Punkt) => {
    const q = nachPixel(p, lage.zoom);
    return { x: q.x - eckeX, y: q.y - eckeY };
  };
  const von = (x: number, y: number) => ausPixel(eckeX + x, eckeY + y, lage.zoom);

  /* WELCHE KACHELN GEHOLT WERDEN, RECHNET `kachelplan` - und zwar auf DER
     STUFE, DIE WIRKLICH GEHOLT WIRD, und die ist bei doppelter Punktdichte
     eine feinere als die des Fensters. Hier stand die Rechnung fest auf
     `lage.zoom` und mit `KACHEL` als Kantenlänge; damit war die Karte auf
     jedem heutigen Bildschirm halb so scharf wie im alten Dialog. */

  /* DIE BESCHRIFTUNG STEHT AUF DERSELBEN STUFE WIE DER GRUND.
   *
   * ORIGINALTEXT MARVIN (04.09.2026, mit zwei Bildschirmfotos der Stufe 19)
   *   „baue die änderung zurück, die qualität der karte hat sich massiv
   *    verschlechtert. prüfe nochmal die alten scripte um es richtig
   *    umzusetzen."
   *
   * **ER HAT RECHT, UND DIE ALTEN SKRIPTE GEBEN MIR AUCH NICHT RECHT.** In
   * .103 wurde die Beschriftung eine Stufe GRÖBER geholt und aufgezogen -
   * doppelt so grosse Buchstaben, ein Viertel der Anfragen. Der Preis stand
   * auf seinem Bildschirm: eine Kachel, die auf die doppelte Kante gezogen
   * wird, hat halb so viele Bildpunkte je Zentimeter, und eine Hausnummer
   * hat davon von Haus aus wenige.
   *
   * DER ALTE DIALOG HOLT SIE AUF DERSELBEN STUFE: `z = Math.min(wunsch,
   * maxz)` - gröber wird sie nur, wenn der Dienst die feine Stufe GAR NICHT
   * HAT. Das ist etwas anderes als „grundsätzlich gröber": das eine ist ein
   * Rückfall, das andere ein Vergrösserungsglas.
   *
   * **EINE SCHRIFT, DIE IN EIN BILD GEBRANNT IST, WIRD NICHT GRÖSSER,
   * SONDERN NUR GRÖBER.** Wer sie vergrössern will, muss näher heranzoomen -
   * dann holt der Dienst wirklich mehr Bildpunkte. */

  /* DIE ADRESSE EINER KACHEL WIRD IN `ebeneZeichnen` GEBAUT, an EINER Stelle.
     Hier standen drei Abschriften derselben drei Ersetzungen - eine je Lage -,
     und die dritte hätte beim nächsten Nachtrag anders gelautet. */
  // Welche Kacheln liegen unter der Zeichnung: Luftbild, Straßenkarte, keine.
  // ABGETASTET wird nur das Luftbild - aus einer Straßenkarte lässt sich keine
  // Oberfläche schätzen, und das sagt die Zeile unter der Karte.
  /* WAS DER BENUTZER GEWÄHLT HAT ... */
  const gewuenscht: 'luftbild' | 'strasse' | 'keins' =
    luftbild && luftbildweg ? 'luftbild'
      : !luftbild && strassenweg ? 'strasse' : 'keins';

  /* ... UND WAS WIRKLICH DARUNTERLIEGT.
   *
   * ORIGINALTEXT MARVIN (03.09.2026, mit zwei Bildschirmfotos einer grauen
   * Fläche, Luftbild ausgeschaltet)
   *   „die karte geht immernoch nicht. behebe nun final den fehler."
   *
   * Der Straßenkarten-Dienst des Bundes war von seinem Server aus nicht
   * erreichbar. Der Kasten sagte das seit .93 auch - und darunter blieb
   * trotzdem eine leere Fläche, obwohl das LUFTBILD desselben Ausschnitts
   * einwandfrei kommt: die Landesdienste antworten, es hing an genau einem
   * fremden Host.
   *
   * **Ein Dienst, der nicht antwortet, hält nichts auf.** Kommt von der
   * Straßenkarte keine einzige Kachel, tritt das Luftbild an ihre Stelle -
   * und zwar SICHTBAR: unter der Karte steht, dass es das Luftbild ist und
   * warum. Ein stiller Rückfall wäre der schlimmste Ausgang; dann sucht
   * jemand den Fehler in einem Dienst, der längst umgangen wird.
   *
   * Der Rückfall gilt für diese Ansicht, nicht für immer: beim nächsten
   * Öffnen wird der eingetragene Dienst wieder gefragt. Ein Dienst, der eine
   * Stunde weg war, soll nicht bis zum Abmelden weg bleiben. */
  const [strasseWeg, setStrasseWeg] = useState(false);
  /* UND DER RÜCKFALL GILT IN BEIDE RICHTUNGEN.
   *
   * ORIGINALTEXT MARVIN (03.09.2026, mit fünf Bildschirmfotos)
   *   „es läd sehr langsam, bzw garnicht. straßennamen und hausnummern stehen
   *    auch nicht drauf, das problem ist immernoch da."
   *
   * Seit .95 tritt das Luftbild ein, wenn die Straßenkarte ausfällt. Die
   * ANDERE Richtung fehlte - und sie ist die, die er auf dem Bildschirm
   * hatte: fällt der Landesdienst aus, blieb eine graue Fläche stehen,
   * obwohl die Straßenkarte desselben Ausschnitts kommt. **Und sie trägt die
   * Straßennamen und Hausnummern selbst**, also genau das, was er vermisst.
   *
   * Ein Luftbild ist für den Behördenantrag das bessere Bild und für die
   * Oberflächenschätzung das einzige. Eine Karte MIT Straßennamen ist
   * trotzdem besser als keine - und dass es die Ersatzkarte ist, steht
   * darunter. Ein stiller Rückfall wäre der schlimmste Ausgang.
   *
   * DIE BEIDEN MERKER SPERREN SICH GEGENSEITIG: fällt danach auch der Ersatz
   * aus, wird NICHT zurückgesprungen. Ohne diesen Riegel liefen die beiden
   * Rückfälle im Kreis, und die Karte flackerte zwischen zwei leeren Flächen.
   */
  const [luftbildWeg, setLuftbildWeg] = useState(false);
  const grund: 'luftbild' | 'strasse' | 'keins' =
    gewuenscht === 'strasse' && strasseWeg && luftbildweg && !luftbildWeg
      ? 'luftbild'
      : gewuenscht === 'luftbild' && luftbildWeg && strassenweg && !strasseWeg
        ? 'strasse'
        : gewuenscht;
  const ersatzLuftbild = gewuenscht === 'strasse' && grund === 'luftbild';
  const ersatzStrasse = gewuenscht === 'luftbild' && grund === 'strasse';

  /* WAS AUS DEM DURCHGANG HERAUSKAM, IN DREI AUSGÄNGEN.
     Die Zählung kommt seit .105 aus dem Zeichnen selbst: `fetch` antwortet
     immer - mit einem Bild, mit einem 204 oder mit einem Fehler -, und danach
     steht fest, wie viele Kacheln der Ausschnitt hatte und wie viele davon
     ein Bild gebracht haben. Der Geduldsmesser von .96 ist damit weg: er war
     die Antwort auf ein `<img>`, das auf einem hängenden Weg NIE meldet. */
  const grundLeer = grund !== 'keins' && stand.fertig && !stand.da;
  /* UND DER HALBE FALL IST EIN EIGENER. Er kommt vor: ein Landesdienst
     antwortet für die eine Kachel und läuft bei der nächsten in seinen
     Zeitüberlauf. Ihn als „alles da" auszugeben verschweigt die Lücke, ihn
     als „nichts da" auszugeben ist schlicht falsch. */
  const kachelLuecke = grund !== 'keins' && stand.fertig && stand.da
    && stand.gesamt > 0 && stand.gut < stand.gesamt;
  // Die Beschriftung liegt nur ÜBER dem Luftbild: die Straßenkarte trägt ihre
  // eigene, und zweimal übereinander ist sie unleserlich.
  const schriftDa = grund === 'luftbild' && beschriftung && !!beschriftungsweg;

  const schriftLeer = schriftDa && schriftStand.fertig && !schriftStand.da;
  const schriftLuecke = schriftDa && schriftStand.fertig && schriftStand.da
    && schriftStand.gesamt > 0 && schriftStand.gut < schriftStand.gesamt;

  /* IST WIRKLICH NICHTS ANGEKOMMEN, WIRD GEFRAGT WARUM.
     Erst dann: der Weg dahinter holt ein echtes Testbild vom Landesdienst.
     Und je Grundlage nur einmal - ein zweiter Abruf beim Ziehen wäre genau
     die Sorte Arbeit, die den Server in eine Schlange stellt. */
  useEffect(() => {
    if (!grundLeer || !kartenbefund) return;
    if (warum?.was === grund) return;
    let weg = false;
    fetch(`${kartenbefund}?was=${grund === 'strasse' ? 'strasse' : 'luftbild'}`,
          { credentials: 'same-origin' })
      .then((a) => (a.ok ? a.json() : null))
      .then((d) => {
        if (weg || !d) return;
        const satz = !d.eingetragen
          ? (d.land
              ? `Für ${d.land} ist kein Dienst eingetragen.`
              : 'Zu dieser Postleitzahl gehört kein bekanntes Bundesland, '
                + 'also auch kein Dienst.')
          : String(d.meldung ?? 'Der Dienst hat kein Bild geliefert.');
        setWarum({ was: grund,
                   text: satz + (d.woran ? ` (${d.woran})` : '') });
      })
      .catch(() => { /* Ein Befund, der selbst nicht kommt, hält nichts auf. */ });
    return () => { weg = true; };
  }, [grundLeer, grund, kartenbefund]);

  /* UND DASSELBE FÜR DIE BESCHRIFTUNG. Der Weg kennt seit .100 `was=
     beschriftung`; bis dahin liess sich für diese Lage gar kein Grund
     anzeigen - **ein Bildschirm, der eine Lücke nicht benennen kann, hat
     keine Lücke, sondern einen Fehler.** */
  useEffect(() => {
    if (!schriftLeer || !kartenbefund || warumSchrift) return;
    let weg = false;
    fetch(`${kartenbefund}?was=beschriftung`, { credentials: 'same-origin' })
      .then((a) => (a.ok ? a.json() : null))
      .then((d) => {
        if (weg || !d) return;
        setWarumSchrift(!d.eingetragen
          ? (d.land
              ? `Für ${d.land} ist kein Beschriftungs-Dienst eingetragen.`
              : 'Zu dieser Postleitzahl gehört kein bekanntes Bundesland, '
                + 'also auch kein Dienst.')
          : String(d.meldung ?? 'Der Dienst hat kein Bild geliefert.')
            + (d.woran ? ` (${d.woran})` : ''));
      })
      .catch(() => { /* Ein Befund, der selbst nicht kommt, hält nichts auf. */ });
    return () => { weg = true; };
  }, [schriftLeer, kartenbefund, warumSchrift]);

  /* UND IM SELBEN ATEMZUG WIRD UMGESCHALTET. Der Befund oben wird trotzdem
     geholt - er läuft schon, wenn dieser Effekt greift, und seine Antwort
     trägt `was: 'strasse'`. Sie steht danach unter der Karte, obwohl darüber
     längst das Luftbild liegt: **der Grund gehört zu dem Dienst, der ausfiel,
     nicht zu dem, der eingesprungen ist.** */
  useEffect(() => {
    if (gewuenscht === 'strasse' && grund === 'strasse' && grundLeer
        && luftbildweg) {
      setStrasseWeg(true);
    }
    if (gewuenscht === 'luftbild' && grund === 'luftbild' && grundLeer
        && strassenweg) {
      setLuftbildWeg(true);
    }
  }, [gewuenscht, grund, grundLeer, luftbildweg, strassenweg]);

  /* ================================================================== *
   *  DAS ZEICHNEN DER KARTE - 1:1 aus `ebene()` und `kartenzeichnen()`   *
   * ================================================================== *
   *
   * ORIGINALTEXT MARVIN (04.09.2026)
   *   „ohne witz, die qualität ist immernoch schlecht und es ist immernich
   *    nicht so wie es war. deine aufgabe ist jetzt das alte system, scripte
   *    auszulesen und dann alles 1:1 in der neuen plattform einzubinden"
   *
   * **DIE KACHELN STANDEN BIS .104 ALS `<img>` MIT 256 PIXELN KANTE IN DER
   * SEITE.** Ein `<img>` hat keine Punktdichte: der Browser zieht es auf die
   * Bildpunkte des Bildschirms hoch. Auf einem Gerät mit doppelter Dichte
   * sind das halb so viele Bildpunkte je Zentimeter wie im alten Dialog, auf
   * einem 4K-Schirm ein Viertel.
   *
   * Der alte Dialog macht zwei Dinge, und sie gehören zusammen:
   *
   *   1. Seine Zeichenflächen sind `BREIT*DPR` breit und tragen
   *      `setTransform(DPR,0,0,DPR,0,0)`: sie rechnen in Anzeigepunkten und
   *      speichern in echten Bildpunkten.
   *   2. `var wunsch = ZOOM + (DPR >= 1.5 ? 1 : 0);` - bei doppelter Dichte
   *      wird die FEINERE Stufe geholt und halb so gross gezeichnet.
   *
   * Beides steht jetzt hier. Und das Zeichenfeld ist nicht mehr das ZWEITE
   * Feld neben den Bildern, sondern DAS Feld: dasselbe Bild wird angesehen
   * und abgetastet, so wie im alten Dialog auch.
   *
   * GEHOLT WIRD MIT `fetch` UND NICHT MIT `<img>`, und das löst nebenbei zwei
   * alte Übel: der GRUND einer leeren Kachel steht im Kopf `x-mds-grund` und
   * war für ein `<img>` unerreichbar, und die STUFE, auf der der Dienst
   * wirklich liefert, steht seit dieser Version im Kopf `x-mds-stufe` - damit
   * fragt das Fenster nicht länger eine Stufe, die es beim Dienst nicht gibt. */
  const dprStand = useRef(1);
  const [dpr, setDpr] = useState(() =>
    punktdichte(typeof window === 'undefined' ? 1 : window.devicePixelRatio));
  dprStand.current = dpr;
  /* EIN FENSTER, DAS AUF EINEN ANDEREN BILDSCHIRM GEZOGEN WIRD, ÄNDERT SEINE
     PUNKTDICHTE. Der alte Dialog liest sie bei jedem Zeichnen neu (`groesse()`);
     hier meldet der Browser es über eine Medienabfrage, und erst dann wird neu
     gezeichnet - ein Zeichnen bei jedem Mausklick wäre teurer als die
     Auskunft wert ist. */
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    let weg = false;
    let ab: (() => void) | null = null;
    const sehen = () => {
      if (weg) return;
      const jetzt = punktdichte(window.devicePixelRatio);
      setDpr((alt) => (alt === jetzt ? alt : jetzt));
      ab?.();
      const m = window.matchMedia(
        `(resolution: ${window.devicePixelRatio}dppx)`);
      const h = () => sehen();
      m.addEventListener('change', h);
      ab = () => m.removeEventListener('change', h);
    };
    sehen();
    return () => { weg = true; ab?.(); };
  }, []);

  const schriftUrlV = beschriftungsweg ?? '';
  const grundUrlV = grund === 'strasse' ? (strassenweg ?? '')
    : grund === 'luftbild' ? (luftbildweg ?? '') : '';

  useEffect(() => {
    let lebt = true;
    const c = grundFeld.current;
    if (!c) return;
    const g = c.getContext('2d', { willReadFrequently: true });
    if (!g) return;

    /* `groesse()` des alten Dialogs: die Fläche in echten Bildpunkten, die
       Rechnung in Anzeigepunkten. */
    const gross = (k: HTMLCanvasElement, ktx: CanvasRenderingContext2D) => {
      k.width = Math.max(1, Math.round(breite * dpr));
      k.height = Math.max(1, Math.round(hoehe * dpr));
      ktx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const leeren = (ktx: CanvasRenderingContext2D, farbe: string | null) => {
      ktx.clearRect(0, 0, breite, hoehe);
      if (farbe) { ktx.fillStyle = farbe; ktx.fillRect(0, 0, breite, hoehe); }
    };

    gross(c, g);
    // Der Grundton des alten Dialogs: `kL.fillStyle='#dfe3e8'`.
    leeren(g, '#dfe3e8');

    const sk = schriftFeld.current;
    const sg = sk ? sk.getContext('2d') : null;
    if (sk && sg) { gross(sk, sg); leeren(sg, null); }

    /* DAS VORIGE BILD BLEIBT STEHEN, BIS DAS NEUE DA IST.
     *
     * Es zeigt dieselbe Gegend; was sich geändert hat, ist der Ausschnitt.
     * Also wird es an die Stelle gezeichnet, an der es in der NEUEN Lage
     * liegt: um den Unterschied der beiden Mitten versetzt und, bei einem
     * Stufenwechsel, um `2^(neu-alt)` vergrössert. Danach kommt das neue
     * Bild darüber.
     *
     * **DAS IST ZUGLEICH DER GRUND, WARUM DER SCHUB HIER FALLEN DARF.** Die
     * Verschiebung der Lagen geht auf null zurück und der Inhalt wandert im
     * selben Zug an dieselbe Stelle - für das Auge bewegt sich nichts. Ohne
     * das eine spränge die Karte, ohne das andere bliebe sie grau.
     *
     * NUR AUS DERSELBEN QUELLE. Ein Luftbild als Platzhalter für die
     * Straßenkarte wäre eine Auskunft über eine Lage, die gar nicht gefragt
     * ist - der Leser hält den Schalter dann für kaputt. */
    const uebergang = (
      ktx: CanvasRenderingContext2D,
      alt: Vorbild | null, quelle: string
    ) => {
      if (!alt || alt.quelle !== quelle || !quelle) return;
      const f = 2 ** (lage.zoom - alt.zoom);
      const a = nachPixel(alt.mitte, lage.zoom);
      const m = nachPixel(lage.mitte, lage.zoom);
      const bw = breite * f, bh = hoehe * f;
      ktx.drawImage(alt.bild,
                    (a.x - m.x) + breite / 2 - bw / 2,
                    (a.y - m.y) + hoehe / 2 - bh / 2, bw, bh);
    };
    uebergang(g, vorGrund.current, grundUrlV);
    if (sg) uebergang(sg, vorSchrift.current, schriftDa ? schriftUrlV : '');
    setSchub(null);

    /** Was gezeichnet dasteht, für den nächsten Übergang aufheben. */
    const merken = (
      feld: HTMLCanvasElement, ziel: { current: Vorbild | null },
      quelle: string
    ) => {
      try {
        const k = document.createElement('canvas');
        k.width = feld.width; k.height = feld.height;
        const kg = k.getContext('2d');
        if (!kg) return;
        kg.drawImage(feld, 0, 0);
        ziel.current = { bild: k, mitte: lage.mitte, zoom: lage.zoom, quelle };
      } catch { /* Ein Übergang ist Höflichkeit und hält nichts auf. */ }
    };

    /* BEIDE LAGEN GEHEN NEBENEINANDER HINAUS UND NICHT NACHEINANDER.
       Es sind zwei Anfragen; hintereinander wartet die Beschriftung auf den
       Grund, und auf Marvins Bildschirm hiess das: erst kommt die Karte, dann
       nach Sekunden die Schrift. Sie kosten dieselbe Zeit nebeneinander. */
    (async () => {
      const grundLauf = (async (): Promise<Bildstand> => {
        if (!grundUrlV) return { ...LEER_STAND, fertig: true };
        return bildZeichnen(g, grundUrlV, lage.mitte, lage.zoom,
                            breite, hoehe, dpr, () => lebt);
      })();

      const schriftLauf = (async (): Promise<Bildstand | null> => {
        if (!sk || !sg) return null;
        if (!schriftDa || !schriftUrlV) return { ...LEER_STAND, fertig: true };
        /* DIE ROHE LAGE GEHT IN EIN EIGENES FELD, und erst danach wird sie
           VERSTÄRKT. Wörtlich `schriftVerstaerken()`: dieselbe Lage zweimal,
           einmal normal und einmal multiplizierend. Weiss bleibt dabei weiss -
           Papier verändert sich nicht -, aber jede Linie und jeder Buchstabe
           verdoppelt seine Tiefe. Es kostet KEINE zweite Anfrage: dasselbe
           Feld, zweimal gezeichnet. */
        const roh = document.createElement('canvas');
        roh.width = sk.width; roh.height = sk.height;
        const rg = roh.getContext('2d');
        if (!rg) return { ...LEER_STAND, fertig: true };
        rg.setTransform(dpr, 0, 0, dpr, 0, 0);
        const sb = await bildZeichnen(rg, schriftUrlV, lage.mitte, lage.zoom,
                                      breite, hoehe, dpr, () => lebt);
        if (!lebt || !sb.da) return sb;
        sg.clearRect(0, 0, breite, hoehe);
        sg.globalCompositeOperation = 'source-over';
        sg.drawImage(roh, 0, 0, breite, hoehe);
        /* DER ZWEITE ABZUG NUR DORT, WO ER TRÄGT.
         *
         * ORIGINALTEXT MARVIN (04.09.2026)
         *   „zeigt straße und hausnummer nicht valide an. damit kann ich
         *    nciht arbeiten."
         *
         * Der zweite, multiplizierende Abzug verdoppelt die Tiefe jeder
         * Linie - solange die Tinte DUNKEL ist. Bei einer hellen Tinte tut
         * er das Gegenteil: Weiss mal Weiss bleibt Weiss, und darunter
         * multipliziert die Fläche gleich noch einmal gegen das Luftbild.
         * **Die amtliche Liegenschaftskarte von Hessen zeichnet WEISS**, und
         * gegen ein Luftbild multipliziert ist sie da und unsichtbar.
         *
         * Gemessen hat das der Server (`lagenbefund` in `kachelbild.ts`),
         * entschieden auch er (`zeichenart`); hier steht das Ergebnis. Ohne
         * Messung gilt der Regelfall von .100 bis .107. */
        /* **UND NUR DORT, WO DIE LAGE AUS STRICHEN BESTEHT.**
         *
         * ORIGINALTEXT MARVIN (04.09.2026)
         *   „die häuser sind was dunkel. hausnummern sind schwer zu lesen."
         *
         * `GebaeudeBauwerke` FÜLLT die Gebäudeflächen (gemessen 22,9 %
         * Deckung). Der zweite Abzug verdoppelt die Tiefe der Füllung
         * genauso wie die der Ziffer - danach liegt über jedem Dach eine
         * doppelt multiplizierte Fläche, und die Hausnummer steht auf
         * schwarzem Grund. **Der Handgriff, der die Ziffer lesbar machen
         * soll, ist derselbe, der sie unlesbar macht.** Gemessen wird es auf
         * dem Server (`verstaerken` in `kachelbild.ts`). */
        if (sb.art !== 'normal' && sb.verstaerken) {
          sg.globalCompositeOperation = 'multiply';
          sg.drawImage(roh, 0, 0, breite, hoehe);
        }
        sg.globalCompositeOperation = 'source-over';
        return sb;
      })();

      const [befund, sb] = await Promise.all([grundLauf, schriftLauf]);
      if (!lebt) return;
      setStand(befund);
      if (sb) setSchriftStand(sb);
      /* NUR EIN BILD, DAS WIRKLICH ANGEKOMMEN IST, WIRD AUFGEHOBEN. Ein
         leeres Feld als Übergang wäre dasselbe Grau, nur eine Anfrage
         später. */
      if (befund.da) merken(c, vorGrund, grundUrlV);
      if (sk && sb?.da) merken(sk, vorSchrift, schriftUrlV);

      /* ABGETASTET WIRD NUR DAS LUFTBILD - aus einer Straßenkarte lässt
         sich keine Oberfläche schätzen. Und nur, wenn wirklich etwas
         angekommen ist: ein leeres Feld liefert lauter Schwarz, und daraus
         hat die erste Fassung brav „Beton, 40 %" geschätzt. **Ein Vorschlag
         aus dem Nichts ist schlimmer als kein Vorschlag, weil ihn jemand
         glaubt.** */
      if (grund !== 'luftbild' || !befund.da) {
        setLesbar(false); aufProbe?.(null);
        return;
      }
      try {
        g.getImageData(0, 0, 1, 1);
        setLesbar(true);
        const eckeX2 = nachPixel(lage.mitte, lage.zoom).x - breite / 2;
        const eckeY2 = nachPixel(lage.mitte, lage.zoom).y - hoehe / 2;
        const d0 = dprStand.current;
        aufProbe?.((p: Punkt) => {
          const q = nachPixel(p, lage.zoom);
          const x = q.x - eckeX2, y = q.y - eckeY2;
          /* `punkteLesen()` des alten Dialogs: **getImageData kennt die
             Skalierung nicht - es liest immer echte Bildpunkte.** Wer den
             Kern nicht mitskaliert, tastet auf einem Schirm mit doppelter
             Dichte eine halb so grosse Fläche ab. */
          const sK = kernkante(5, d0);
          const px = Math.round(x * d0) - (sK >> 1);
          const py = Math.round(y * d0) - (sK >> 1);
          if (px < 0 || py < 0
              || px + sK > c.width || py + sK > c.height) return null;
          const d = g.getImageData(px, py, sK, sK).data;
          const raus: number[] = [];
          for (let i = 0; i < d.length; i += 4) {
            raus.push(d[i], d[i + 1], d[i + 2]);
          }
          return raus;
        });
      } catch {
        setLesbar(false); aufProbe?.(null);
      }
    })();

    return () => { lebt = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lage.mitte.lat, lage.mitte.lng, lage.zoom, breite, hoehe, dpr,
      grund, grundUrlV, schriftDa, schriftUrlV]);

  const mpp = meterJePixel(lage.mitte.lat, lage.zoom);
  /* Der Balken darf höchstens ein knappes Drittel der Karte breit sein: auf
     280 Bildpunkten wären feste 140 die halbe Karte, und ein Maßstab, der die
     Zeichnung verdeckt, misst nichts mehr. */
  const mass = massstab(mpp, Math.min(140, Math.round(breite * 0.3)));

  /* Was über diesen Ausschnitt zu sagen ist, gesammelt statt aneinandergehängt.
     Vorher stand jeder Satz mit einem eigenen „, " davor im JSX - fiel der
     erste weg, fing die Zeile mit einem Trennpunkt an. */
  const saetze: string[] = [];
  /* HIER STAND EINE ZAHL, UND EIN PRÜFSTAND HAT SIE WIEDER HERAUSGEHOLT.
   *
   * ORIGINALTEXT MARVIN (04.09.2026)
   *   „und ich bin der meinung das die geodaten noch schärfer sind von den
   *    bilder als das was du eingebaut hats. prüfe das gegen"
   *
   * Der erste Anlauf hat „19 cm je Bildpunkt" unter die Karte geschrieben.
   * **Prüfstand 086 ist zu Recht rot geworden:** genau diese Sorte Zeile hat
   * Marvin am 03.09.2026 wegnehmen lassen („«Näher/weiter» sollte ein balken
   * sein mit einem maßstab"), weil man mit ihr rechnen muss, statt sie
   * anzulegen. Der Maßstabsbalken beantwortet „wie weit ist das da drüben"
   * besser als jede Zahl.
   *
   * **SEINE FRAGE IST EINE ANDERE, UND SIE IST EINE PRÜFUNG:** „prüfe das
   * gegen". Die Bodenauflösung steht deshalb dort, wo geprüft wird - unter
   * „Dienste prüfen", zusammen mit der Messung, ob die Quelle auf dieser
   * Stufe überhaupt noch eigene Einzelheiten trägt. **Eine Antwort auf eine
   * Prüffrage gehört auf den Prüfbildschirm und nicht in die Arbeit.** */
  if (grund === 'strasse' && !grundLeer) {
    saetze.push('Straßenkarte, Oberflächen nur von Hand');
  }
  // DER RÜCKFALL SAGT SICH AN. Sonst sieht der Leser ein Luftbild, wo er eine
  // Straßenkarte gewählt hat, und hält den Schalter für kaputt.
  if (ersatzLuftbild && !grundLeer) {
    saetze.push('die Straßenkarte antwortet nicht, hier steht das Luftbild');
  }
  if (grund === 'keins' && !luftbild) {
    saetze.push('ohne Luftbild ist keine Karte hinterlegt');
  }
  if (grund === 'luftbild' && !grundLeer && lesbar === false) {
    saetze.push('Oberflächen nur von Hand');
  }
  // EINE WEISSE FLÄCHE MUSS SAGEN, WARUM SIE WEISS IST.
  if (grundLeer) {
    saetze.push(grund === 'strasse'
      ? 'die Straßenkarte antwortet nicht'
      : 'das Luftbild antwortet nicht');
  }
  // DER RÜCKFALL IN DIE ANDERE RICHTUNG SAGT SICH GENAUSO AN - und er sagt
  // dazu, was er BRINGT: die Straßennamen. Genau die hat Marvin vermisst.
  if (ersatzStrasse && !grundLeer) {
    saetze.push('das Luftbild antwortet nicht, hier steht die Straßenkarte '
                + 'mit den Straßennamen');
  }
  // UND DER HALBE FALL STEHT MIT SEINER ZAHL DA. „Es fehlt etwas" ohne Zahl ist
  // keine Auskunft; „7 von 12" sagt, ob man weiterarbeiten kann.
  if (kachelLuecke) {
    saetze.push(`Bild aus ${stand.gut} von ${stand.gesamt} Kacheln`);
  }
  /* WIE VIEL SCHÄRFE HIER FEHLT, UND WARUM.
   *
   * ORIGINALTEXT MARVIN (04.09.2026)
   *   „zum 2. ist die karte noch sehr verpixelt, wie brauchen eine bessere
   *    qualität ... auch die streaßennamen und hausnummern sind nicht lesbar
   *    oder nur schwer. es ist nicht zufriedenstellend."
   *
   * **DIE URSACHE IST EINE EIGENSCHAFT DER QUELLE UND KEINE EINSTELLUNG.**
   * Die freien Kartendienste, die deutsche Straßennamen und Hausnummern
   * tragen, enden bei Stufe 19; das Fenster steht ab Werk auf 20. Darüber
   * gibt es keine feineren Bildpunkte, es gibt nur grössere - **eine Schrift,
   * die in ein Bild gebrannt ist, wird durch Vergrössern nicht grösser,
   * sondern gröber.** Das stand seit .104 in der Anweisung und war nirgends
   * ZU SEHEN.
   *
   * Es steht jetzt da, mit dem Ausweg daneben: eine Stufe herausgehen bringt
   * die Schrift in ihre eigene Auflösung zurück. **Ein Mangel, den man
   * benennen kann, ist etwas anderes als ein Bild, das einfach schlecht
   * aussieht.** */
  const schaerfe = schriftDa && schriftStand.da && schriftStand.schaerfe
    ? schriftStand.schaerfe
    : (stand.da ? stand.schaerfe : null);
  /* EINE LAGE, DIE AUSFÄLLT, SAGT ES - auch wenn die Karte darunter steht.
     Vorher blieb der Haken gesetzt und nichts geschah; **ein Haken, der
     nichts bewirkt und nichts sagt, ist eine Falle.** */
  if (schriftLeer) {
    saetze.push('Straßennamen und Hausnummern kommen gerade nicht');
  } else if (schriftLuecke) {
    saetze.push(`Beschriftung aus ${schriftStand.gut} von `
                + `${schriftStand.gesamt} Kacheln`);
  }
  /* WELCHE LAGE WIE VIEL LIEFERT - GEMESSEN, NICHT BEHAUPTET.
   *
   * ORIGINALTEXT MARVIN (04.09.2026)
   *   „das was jetzt eingebunden ist macht immernoch fehler ... zeigt straße
   *    und hausnummer nicht valide an. damit kann ich nciht arbeiten."
   *
   * Ob ein eingetragener Layer wirklich etwas hergibt, war bis .107 nur an
   * der Karte selbst zu sehen - und ein Layername, den es gar nicht gibt,
   * sieht dort aus wie eine Beschriftung, die einfach ausbleibt. Die Zahl
   * steht deshalb da: **„4 % gedeckt" und „nichts gekommen" sind zwei
   * verschiedene Auskünfte mit zwei verschiedenen nächsten Schritten.**
   *
   * Nur bei einer DURCHSICHTIGEN Lage: bei einer Vollkarte ist „100 %
   * gedeckt" keine Auskunft, sondern eine Selbstverständlichkeit, und eine
   * Zeile, die immer dasteht, wird nicht gelesen. */
  if (schriftDa && schriftStand.da && schriftStand.deckung !== null
      && schriftStand.deckung < 60) {
    saetze.push(`Beschriftung ${schriftStand.deckung
      .toFixed(1).replace('.', ',')} % gedeckt`
      /* WAS MIT DEM BILD GESCHEHEN IST, STEHT DA. Gedämpft heisst: die
         Gebäudefüllung ist auf ein Viertel geschwächt, damit die Dächer im
         Luftbild hell bleiben - und die Schrift steht in voller Stärke mit
         einem hellen Saum darauf. Wer die Karte beurteilt, muss wissen, was
         er vor sich hat. */
      + (schriftStand.gedaempft
          ? `, Füllung gedämpft (${(schriftStand.fuellung ?? 0)
              .toFixed(0)} % war Füllung)`
          : schriftStand.art === 'normal' ? ', hell gezeichnet' : ''));
  }
  /* DIE GRENZPUNKTE: gedeckelt an dem, was sie KOSTEN, und nicht an der Zahl
     der Flurstücke. Die Karte und der Satz darunter fragen DIESELBE Zahl -
     zwei Rechnungen über dieselbe Frage laufen beim ersten Nachtrag
     auseinander, und dann steht unter der Karte etwas anderes, als auf ihr
     zu sehen ist. */
  const grenzpunktZahl = flurstuecke?.length ? grenzpunkteZaehlen(flurstuecke) : 0;
  const grenzpunkteDa = grenzpunkteZeigen(lage.zoom, grenzpunktZahl);
  const flurSatz = grenzpunktSatz(flurstuecke?.length ?? 0, lage.zoom, grenzpunktZahl);
  if (flurSatz) saetze.push(flurSatz);

  /* MIT DEM MAUSRAD ZOOMEN, UND ZWAR AUF DEN ZEIGER.
   *
   * ORIGINALTEXT MARVIN (25.08.2026)
   *   „ich kann bei den karten nicht mit dem mausrad zoomen"
   *
   * ORIGINALTEXT MARVIN (02.09.2026)
   *   „immer das scrollen funktioniert nicht richtig, es verschiebt dann die
   *    karte in dem popup. das ist unglücklich gebaut"
   *
   * HIER STAND `onWheel` IM JSX, UND DER KOMMENTAR DANEBEN WAR FALSCH. Er
   * behauptete, React hänge den Zuhörer nicht passiv an, solange
   * `preventDefault` gerufen wird. Das Gegenteil ist der Fall: React hängt
   * `wheel` (wie `touchstart` und `touchmove`) am Wurzelknoten PASSIV an, und
   * ein passiver Zuhörer darf nicht abbrechen - `preventDefault()` wird
   * stillschweigend übergangen, ohne Fehler und ohne Meldung. Die Karte
   * zoomte also UND die Seite rollte darunter weg; im Fenster „Auftrag
   * vorbereiten" hiess das: der Ausschnitt springt und man sucht die Karte
   * wieder.
   *
   * EIN NICHT PASSIVER ZUHÖRER LÄSST SICH NUR VON HAND ANHÄNGEN, und das geht
   * nur über `addEventListener(..., { passive: false })` am Knoten selbst.
   * Deshalb steht er in einem Effekt und nicht im JSX.
   *
   * Der Effekt hängt an NICHTS: was er braucht, liest er beim Auslösen aus
   * einem Merker. Eine Abhängigkeitsliste mit `lage` und `setzeLage` darin
   * hiesse, den Zuhörer bei jedem Zeichnen ab- und wieder anzumelden - und
   * `setzeLage` ist an jeder Aufrufstelle ein an Ort und Stelle gebauter
   * Pfeil, also bei jedem Zeichnen ein neues Objekt. Dieselbe Falle wie in
   * `Dialog` und in `useEntwurf`.
   *
   * Der Punkt UNTER DEM ZEIGER bleibt stehen: das ist der ganze Trick und der
   * Unterschied zwischen „zoomen" und „suchen, wo es hingesprungen ist".
   */
  const radStand = useRef({ lage, breite, hoehe, setzeLage });
  radStand.current = { lage, breite, hoehe, setzeLage };

  useEffect(() => {
    const knoten = rahmen.current;
    if (!knoten) return;
    function amRad(e: WheelEvent) {
      e.preventDefault();
      const s = radStand.current;
      const richtung = e.deltaY < 0 ? 1 : -1;
      // DIE GRENZE STEHT AN EINER STELLE. Hier stand 14 bis 21, an den beiden
      // Knöpfen 15 bis 22 und im Server nochmal 15 bis 22 - siehe
      // `teile/kartenmass.ts`.
      const neuZoom = zoomKlemmen(s.lage.zoom + richtung);
      if (neuZoom === s.lage.zoom) return;
      const r = knoten!.getBoundingClientRect();
      const m = nachPixel(s.lage.mitte, s.lage.zoom);
      // Wo der Zeiger steht, gemessen von der Kartenmitte.
      const zx = e.clientX - r.left - s.breite / 2;
      const zy = e.clientY - r.top - s.hoehe / 2;
      const zeiger = ausPixel(m.x + zx, m.y + zy, s.lage.zoom);
      const zeigerNeu = nachPixel(zeiger, neuZoom);
      s.setzeLage({
        zoom: neuZoom,
        mitte: ausPixel(zeigerNeu.x - zx, zeigerNeu.y - zy, neuZoom),
      });
    }
    knoten.addEventListener('wheel', amRad, { passive: false });
    return () => knoten.removeEventListener('wheel', amRad);
  }, []);

  /* `ohnebild` heisst: der Grund ist HELL - die leere Fläche und seit der
     Katasterzeichnung auch die Straßenkarte. Die Klasse `strassenkarte` trug
     die Farben der gelben Linie; die gibt es nicht mehr, und eine Klasse, auf
     die keine Regel mehr zeigt, ist toter Code. */
  const kartenklasse = 'tbkarte'
    + (grund !== 'luftbild' || grundLeer ? ' ohnebild' : '');

  return (
    <div
      ref={rahmen}
      className={kartenklasse}
      style={{ width: breite, height: hoehe }}
      onPointerDown={(e) => {
        (e.target as Element).setPointerCapture?.(e.pointerId);
        ziehStart.current = { x: e.clientX, y: e.clientY };
        setSchub(null);
        setVerschoben(false);
      }}
      onPointerMove={(e) => {
        const a = ziehStart.current;
        if (!a) return;
        const dx = e.clientX - a.x, dy = e.clientY - a.y;
        /* Die drei Bildpunkte sind die Grenze zwischen einem Klick mit
           zittriger Hand und einem Zug. Sie gelten EINMAL: ist der Zug erst
           erkannt, folgt die Ansicht auch der kleinsten Bewegung. */
        if (!schub && Math.abs(dx) + Math.abs(dy) < 3) return;
        setVerschoben(true);
        setSchub({ x: dx, y: dy });
      }}
      onPointerUp={(e) => {
        const s = schub;
        ziehStart.current = null;
        /* ERST BEIM LOSLASSEN WIRD GEHOLT, UND ZWAR EINMAL. Der Schub bleibt
           dabei stehen, bis das neue Bild da ist: würde er hier auf null
           gesetzt, spränge die Karte für die Dauer der Anfrage an ihren alten
           Platz zurück. Er fällt weg, sobald der Zeicheneffekt zur neuen Lage
           gelaufen ist. */
        if (s) {
          setzeLage({ ...lage,
            mitte: ausPixel(mitteP.x - s.x, mitteP.y - s.y, lage.zoom) });
          return;
        }
        // Ein Klick, der die Karte verschoben hat, setzt keinen Punkt.
        if (verschoben || !aufKlick) return;
        const r = rahmen.current!.getBoundingClientRect();
        aufKlick(von(e.clientX - r.left, e.clientY - r.top));
      }}
    >
      {/* ZWEI ZEICHENFLÄCHEN, IN ECHTEN BILDPUNKTEN.
       *
       * Sie sind `breite*DPR` mal `hoehe*DPR` gross und werden über CSS auf
       * die Anzeigegrösse gebracht - genau so, wie es der alte Dialog macht.
       * Dieselbe Kachel liegt damit auf einem Gerät mit doppelter Punktdichte
       * mit vier Mal so vielen Bildpunkten da wie in einem `<img>`.
       *
       * DER GRUND WIRD ANGESEHEN UND ABGETASTET. Bis .104 gab es dafür ZWEI
       * Flächen: sichtbare `<img>` und ein verstecktes Zeichenfeld daneben.
       * Zwei Wege über dasselbe Bild laufen beim ersten Nachtrag auseinander,
       * und der eine war der unschärfere.
       *
       * DIE BESCHRIFTUNG LIEGT DARÜBER UND WIRD MULTIPLIZIEREND EINGEBLENDET
       * (`stil.css`, `.tbschrift`): Weiss ist dabei folgenlos, übrig bleiben
       * Linien und Schrift. */}
      {/* ALLES, WAS AM BODEN KLEBT, LIEGT IN EINEM SCHIEBER.
       *
       * Beim Ziehen wandern Grund, Beschriftung, Grenzen UND die Zeichnung
       * gemeinsam mit - sie beschreiben dieselbe Stelle der Erde, und wer
       * eines davon stehenliesse, hätte eine Grube, die neben ihrem Loch
       * liegt. Der Regler und die Sätze darunter gehören zum Fenster und
       * nicht zum Boden; sie stehen ausserhalb. */}
      <div className="tbschieber"
           style={schub
             ? { transform: `translate3d(${schub.x}px, ${schub.y}px, 0)` }
             : undefined}>
      <canvas ref={grundFeld} className="tbgrund" aria-hidden="true"
              style={{ display: grund === 'keins' ? 'none' : 'block' }} />
      {/* DIE ZEICHENART STEHT AM FELD UND NICHT FEST IM STYLESHEET.
       *
       * ORIGINALTEXT MARVIN (04.09.2026)
       *   „zeigt straße und hausnummer nicht valide an."
       *
       * `.tbschrift` trug `mix-blend-mode: multiply` fest, mit dem Satz
       * daneben, das sei „ein Bau für zwei Fälle". Der Satz gilt für DUNKLE
       * Tinte und für nichts sonst: **eine weisse Linie auf durchsichtigem
       * Grund verschwindet beim Multiplizieren restlos**, und genau so
       * zeichnet Hessen seine amtliche Liegenschaftskarte. Welche der beiden
       * Arten gilt, misst der Server am Bild selbst und schickt sie mit
       * (`x-mds-zeichenart`); ohne Messung bleibt es beim Multiplizieren -
       * dem Verhalten von .100 bis .107. */}
      <canvas ref={schriftFeld} className="tbschrift" aria-hidden="true"
              style={{ display: schriftDa ? 'block' : 'none',
                       mixBlendMode: schriftStand.art === 'normal'
                         ? 'normal' : 'multiply' }} />

      {/* DIE GRENZEN LIEGEN UNTER DER ZEICHNUNG, in einem eigenen Feld. Zwei
          getrennte Felder, damit ein Nachladen der Grenzen die Zeichnung nicht
          neu malt: beim Ziehen kommen die Grenzen nach, der gezeichnete Weg
          steht schon. */}
      {!!flurstuecke?.length && (
        <svg width={breite} height={hoehe} className="tbflur">
          {flurstuecke.map((f, i) => (
            <Flurstueckform key={i} f={f} nach={nach} nummern={nummern}
                            aufBild={grund === 'luftbild'} zoom={lage.zoom}
                            punkte={grenzpunkteDa} />
          ))}
        </svg>
      )}

      <svg width={breite} height={hoehe} className="tbzeichnung">
        {kinder(nach, mpp)}
      </svg>
      </div>

      {/* DER REGLER UND DER MASSSTAB, auf BEIDEN Karten und deshalb HIER.
       *
       * ORIGINALTEXT MARVIN (03.09.2026)
       *   „«Näher/weiter» sollte ein balken sein mit einem maßstab auf beiden
       *    karten. die buttons können weg"
       *
       * Die beiden Knöpfe standen zweimal im Haus, in zwei Fassungen mit
       * verschiedenen Grenzen. Ein Regler an der Karte SELBST zeigt zugleich,
       * WO man steht - ein Knopf „näher" sagt das nie -, und er steht damit
       * genau einmal da.
       *
       * DIE ZEIGEREREIGNISSE ENDEN HIER. Die Karte darüber liest jedes
       * Loslassen als Klick und setzt eine Markierung; ohne diese Zeile setzt
       * jeder Griff an den Regler eine Grube mitten in die Zeichnung. */}
      <div className="tbregler"
           onPointerDown={(e) => e.stopPropagation()}
           onPointerMove={(e) => e.stopPropagation()}
           onPointerUp={(e) => e.stopPropagation()}
           onClick={(e) => e.stopPropagation()}>
        <div className="tbzoom">
          <span className="tbzoomtext">Stufe {lage.zoom}</span>
          <input type="range" aria-label="Kartenstufe"
                 min={ZOOM_KLEINSTE} max={ZOOM_GROESSTE} step={1}
                 value={zoomKlemmen(lage.zoom)}
                 onChange={(e) => setzeLage({ ...lage,
                                              zoom: Number(e.target.value) })} />
        </div>
        {/* DER BALKEN IST DIE ANTWORT AUF „WIE WEIT IST DAS DA DRÜBEN".
            Die Zahl ist rund, damit man ihn anlegen kann; die Breite hängt an
            der Karte, denn auf 280 Bildpunkten wären 140 die halbe Karte. */}
        {mass.pixel > 0 && (
          <div className="tbmassstab" style={{ width: mass.pixel }}>
            <span>{mass.text}</span>
          </div>
        )}
      </div>

      {/* WAS ES ÜBER DIESEN AUSSCHNITT ZU SAGEN GIBT - und sonst nichts.
       *
       * Hier standen bis .99 „Stufe 20" und „0,12 m je Bildpunkt" fest an
       * erster Stelle. Die Stufe steht jetzt am Regler (dort ist sie
       * bedienbar), und die Auflösung sagt der Maßstabsbalken, ohne dass
       * jemand multiplizieren muss. Übrig bleiben die SÄTZE, und wenn es
       * keinen gibt, steht auch kein leeres Schild da. */}
      {saetze.length > 0 && (
        <div className="tbstufe">{saetze.join(', ')}</div>
      )}

      {/* WARUM DIE SCHRIFT GROB IST - UND WAS DAGEGEN HILFT.
       *
       * ORIGINALTEXT MARVIN (04.09.2026)
       *   „auch die streaßennamen und hausnummern sind nicht lesbar oder nur
       *    schwer. es ist nicht zufriedenstellend."
       *
       * Der Satz kommt vom SERVER (`schaerfeSatz` in `regeln/kartenbild.ts`):
       * er weiss, aus welcher Stufe das Bild wirklich stammt. Eine zweite
       * Rechnung im Browser liefe beim ersten Nachtrag auseinander, und dann
       * stünde hier eine andere Zahl als die, mit der das Bild gebaut wurde.
       *
       * UND DER KNOPF IST DER AUSWEG. Eine Stufe herausgehen holt die Schrift
       * in ihre eigene Auflösung zurück - **ein Hinweis ohne Weg dahin ist
       * eine Liste von Vorwürfen.** */}
      {schaerfe && !grundLeer && (
        <div className="tbschaerfe">
          <span>{schaerfe}</span>
          {lage.zoom > ZOOM_KLEINSTE && (
            <button type="button" className="knopf klein"
                    onClick={() => setzeLage({ ...lage,
                                               zoom: zoomKlemmen(lage.zoom - 1) })}>
              Eine Stufe herausgehen
            </button>
          )}
        </div>
      )}

      {/* EINE WEISSE FLÄCHE MUSS SAGEN, WARUM SIE WEISS IST - UND ZWAR
          KONKRET. „Der Dienst antwortet nicht" ist dieselbe Auskunft für vier
          verschiedene Ursachen: kein Eintrag für dieses Bundesland, ein
          falscher Layername, ein Netz, das nicht hinausgeht, oder Wartung.
          Der Satz darunter kommt vom SERVER und nennt die, die zutrifft. */}
      {grundLeer && (
        <div className="tbleer">
          {grund === 'strasse'
            ? 'Der Kartendienst hat für diesen Ausschnitt keine einzige Kachel '
              + 'geliefert. Bis dahin hilft das Luftbild.'
            : 'Der Luftbilddienst hat für diesen Ausschnitt keine einzige '
              + 'Kachel geliefert. Gezeichnet werden kann trotzdem.'}
          {/* Der Grund darf auch der des AUSGEFALLENEN Dienstes sein: fällt
              die Straßenkarte aus und das Luftbild danach ebenfalls, ist die
              erste Auskunft die ältere und trotzdem die richtige. */}
          {(warum?.was === grund || warum?.was === gewuenscht) && (
            <div style={{ marginTop: 6 }}><b>Woran es liegt:</b> {warum!.text}</div>
          )}
          {kartenbefund && warum?.was !== grund && warum?.was !== gewuenscht && (
            <div style={{ marginTop: 6 }} className="leise">
              Der Grund wird gerade beim Dienst nachgefragt.
            </div>
          )}
        </div>
      )}

      {/* DIE STRASSENNAMEN FEHLEN, UND DAS STEHT DA - MIT DEM WEG DAHIN.
       *
       * ORIGINALTEXT MARVIN (03.09.2026)
       *   „straßennamen und hausnummern stehen auch nicht drauf, das problem
       *    ist immernoch da."
       *
       * Der Kasten steht NUR, wenn die Karte darunter da ist: liegt ohnehin
       * eine leere Fläche da, ist der obere Kasten die Auskunft, und zwei
       * übereinander sind eine zu viel.
       *
       * UND ER NENNT DEN AUSWEG STATT NUR DES MANGELS. Die Straßenkarte trägt
       * ihre Beschriftung selbst; ein Klick, und die Namen stehen da. Das ist
       * der Unterschied zwischen einer Meldung und einem Vorwurf. */}
      {schriftLeer && !grundLeer && (
        <div className="tbschriftweg">
          <b>Straßennamen und Hausnummern kommen gerade nicht.</b>
          {warumSchrift
            ? <div style={{ marginTop: 4 }}>{warumSchrift}</div>
            : kartenbefund
              ? <div style={{ marginTop: 4 }} className="leise">
                  Der Grund wird gerade beim Dienst nachgefragt.
                </div>
              : null}
          {aufLuftbild && strassenweg && (
            <div style={{ marginTop: 6 }}>
              <button type="button" className="knopf klein"
                      onClick={() => aufLuftbild(false)}>
                Straßenkarte zeigen
              </button>
              <span className="leise" style={{ marginLeft: 8 }}>
                Sie trägt die Namen selbst. Abgetastet werden kann darauf
                nicht.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* DIE BEIDEN DECKEL UND DIE ZÄHLUNG STEHEN IN `teile/grenzpunkte.ts`, ohne
   JSX daneben - siehe den Kopf jener Datei. Hier stand bis .111 ein Deckel
   auf die Zahl der FLURSTÜCKE, und der hat auf Marvins Bildschirm bei 108
   Stücken jeden Grenzpunkt weggenommen. */

/**
 * EIN FLURSTÜCK, GEZEICHNET WIE IM LIEGENSCHAFTSKATASTER.
 *
 * ORIGINALTEXT MARVIN (04.09.2026)
 *   „da will ich die richtigen linien (schwarz meistens), mit den grenzpunkte
 *    (kreise), feiner gezeichnet. so wie es auch in den bauakten der telekom
 *    ist."
 *
 * Hier stand eine gelbe Linie von 1,4 Bildpunkten mit einem weissen Saum von
 * 3,2 darunter - zusammen ein Band von über vier Bildpunkten Breite in einer
 * Farbe, die im Kataster nicht vorkommt. **Eine Grenze, die vier Bildpunkte
 * breit ist, ist auf Stufe 19 einen ganzen Meter breit**, und der Tiefbauer
 * sieht auf dem Papier nicht mehr, auf welcher Seite er steht.
 *
 * Die amtliche Liegenschaftskarte zeichnet es anders, und genau so sieht auch
 * das Papier aus der Bauakte der Telekom aus:
 *
 * ```
 *   die Grenze      eine feine SCHWARZE Linie
 *   der Grenzpunkt  ein kleiner KREIS auf jeder Ecke
 *   die Nummer      klein, in der Fläche, ohne Rahmen
 * ```
 *
 * **DIE GRENZPUNKTE SIND NICHT ERFUNDEN, SIE SIND DIE ECKEN.** Der
 * Landesdienst liefert den Umring als Folge von Punkten, und im Kataster IST
 * jede dieser Ecken ein Grenzpunkt - vermessen, mit einer Kennung, und im
 * Gelände oft ein Stein oder ein Rohr. Wer sie zeichnet, zeichnet nichts
 * dazu; er zeigt, was in den Daten steht.
 *
 * DER SAUM BLEIBT, ABER NUR ÜBER DEM LUFTBILD und nur noch halb so breit:
 * über einem dunklen Dach verschwindet eine schwarze Linie sonst restlos.
 * Auf hellem Grund - Straßenkarte, Liegenschaftskarte, leere Fläche - braucht
 * sie ihn nicht, und dort wäre er ein weisser Streifen über der Karte.
 *
 * Ein Umring kommt als [lng, lat]. Wer das vertauscht, zeichnet im Indischen
 * Ozean: das ist dieselbe Falle wie bei der Achsenreihenfolge des Dienstes.
 */
function Flurstueckform({
  f, nach, nummern, aufBild, zoom, punkte = true,
}: {
  f: Flurstueck;
  nach: (p: Punkt) => { x: number; y: number };
  nummern: boolean;
  aufBild: boolean;
  zoom: number;
  punkte?: boolean;
}) {
  const ringe: number[][][] = [];
  const g = f.geometry;
  if (g?.type === 'Polygon') ringe.push(...(g.coordinates ?? []));
  else if (g?.type === 'MultiPolygon') {
    for (const teil of g.coordinates ?? []) ringe.push(...teil);
  } else return null;

  const wege = ringe.map((r) => r
    .map((c, i) => {
      const q = nach({ lat: c[1], lng: c[0] });
      return `${i ? 'L' : 'M'}${q.x.toFixed(1)} ${q.y.toFixed(1)}`;
    }).join(' ') + ' Z').join(' ');

  // Der Platz für die Nummer: die Mitte des äußeren Umrings.
  const aussen = ringe[0] ?? [];
  let mx = 0, my = 0;
  for (const c of aussen) { const q = nach({ lat: c[1], lng: c[0] }); mx += q.x; my += q.y; }
  const n = aussen.length || 1;

  /* DER LETZTE PUNKT EINES UMRINGS IST DER ERSTE. GeoJSON schliesst den Ring,
     indem es die Anfangsecke am Ende wiederholt; wer sie mitzeichnet, legt
     zwei Kreise übereinander - und auf einem halbdurchsichtigen Kreis sieht
     man das. */
  const ecken: { x: number; y: number }[] = [];
  if (punkte && zoom >= GRENZPUNKTE_AB_STUFE) {
    for (const r of ringe) {
      const bis = r.length > 1
        && r[0][0] === r[r.length - 1][0]
        && r[0][1] === r[r.length - 1][1] ? r.length - 1 : r.length;
      for (let i = 0; i < bis; i++) {
        ecken.push(nach({ lat: r[i][1], lng: r[i][0] }));
      }
    }
  }

  return (
    <g className="flurstueck">
      {aufBild && (
        <path d={wege} fill="none" className="flursaum" />
      )}
      <path d={wege} fill={aufBild ? 'none' : 'rgba(31,56,100,.04)'}
            className="flurlinie" />
      {/* ZWEI PFADE FÜR ALLE PUNKTE DIESES FLURSTÜCKS, nicht ein Knoten je
          Punkt. Ein Wegstück von einem hundertstel Bildpunkt mit runder
          Kappe ist ein Kreis vom Durchmesser der Strichbreite - **damit
          kostet der tausendste Grenzpunkt so viel wie der erste**, und der
          Deckel darüber muss nicht mehr bei jedem zweiten Ausschnitt
          greifen. Der dunkle Ring liegt unten, der helle Kern darüber. */}
      {ecken.length > 0 && (() => {
        const d = ecken
          .map((p) => `M${p.x.toFixed(1)} ${p.y.toFixed(1)}h.01`).join('');
        return (<>
          <path d={d} className="grenzring" />
          <path d={d} className="grenzkern" />
        </>);
      })()}
      {nummern && f.nr && aussen.length > 2 && (
        <text x={mx / n} y={my / n} className="flurnr"
              textAnchor="middle" dominantBaseline="middle">
          {f.nr}
        </text>
      )}
    </g>
  );
}
